import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/atomic/Button';
import Spinner from 'components/atomic/Spinner';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  width: 100%;
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const FilingInfoForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    setValue,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);
  const values = useWatch({ control });
  const prevValues = usePrevious(values);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevValues, values)) {
      if (values[FIELD_KEYS.spouse] === 'no' && !values[FIELD_KEYS.dependents].length) {
        setValue(FIELD_KEYS.filingSingle, 'single');
      }
      if (values[FIELD_KEYS.spouse] === 'yes' && !values[FIELD_KEYS.marriedFilingSeparately]) {
        setValue(FIELD_KEYS.marriedFilingSeparately, 'jointly');
      }
    }
  }, [prevValues, values]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [prevDefaultValues, defaultValues]);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        {values[FIELD_KEYS.spouse] === 'no' && !values[FIELD_KEYS.dependents].length && (
          <Col xs={12} md={6}>
            <SelectField
              {...getFieldProps(FIELD_KEYS.filingSingle)}
              disabled
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
        )}
        {values[FIELD_KEYS.spouse] === 'no' && !!values[FIELD_KEYS.dependents].length && (
          <Col xs={12} md={6}>
            <SelectField
              {...getFieldProps(FIELD_KEYS.filingSingle)}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
        )}
        {values[FIELD_KEYS.spouse] === 'yes' && (
          <Col xs={12} md={6}>
            <SelectField
              {...getFieldProps(FIELD_KEYS.marriedFilingSeparately)}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col xs={12} md={3} />
        <Col xs={12} md={6}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.black}>
            Save Information
          </Button>
        </Col>
        <Col xs={12} md={3} />
      </Row>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Processing...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </StyledForm>
  );
};

FilingInfoForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default FilingInfoForm;
